import { BarChart, Box, Container, Header } from "@amzn/awsui-components-console";
import React, { useMemo } from "react";

import { AnalyticsDailyPoint } from "@/hooks/admin/useAdminUserAnalytics";

import { formatCount, formatDayLabel } from "./format";

interface DailyActivityChartProps {
  points: AnalyticsDailyPoint[];
  /** True while a window is being fetched, so the chart can show a placeholder. */
  loading?: boolean;
  /**
   * Why there is no trend to draw, when that is expected rather than a failure.
   * The all-time window aggregates lifetime totals and keeps no per-day
   * breakdown, so an empty chart there is correct and needs explaining.
   */
  unavailableReason?: string;
}

/**
 * Questions asked and people asking them, per day of the selected window.
 *
 * Two series on one chart because the interesting reading is the relationship:
 * a rise in questions with a flat user count is heavier use by the same people,
 * which is a different story from more people arriving.
 */
const DailyActivityChart: React.FC<DailyActivityChartProps> = ({
  points,
  loading = false,
  unavailableReason,
}) => {
  const series = useMemo(
    () => [
      {
        title: "Questions",
        type: "bar" as const,
        data: points.map((point) => ({
          x: formatDayLabel(point.date),
          y: point.questions,
        })),
        valueFormatter: (value: number) => formatCount(value),
      },
      {
        title: "Active users",
        type: "bar" as const,
        data: points.map((point) => ({
          x: formatDayLabel(point.date),
          y: point.activeUsers,
        })),
        valueFormatter: (value: number) => formatCount(value),
      },
    ],
    [points]
  );

  const xDomain = useMemo(
    () => points.map((point) => formatDayLabel(point.date)),
    [points]
  );

  return (
    <Container header={<Header variant="h2">Daily activity</Header>}>
      {unavailableReason ? (
        <Box
          textAlign="center"
          padding="l"
          color="text-body-secondary"
          data-testid="daily-activity-unavailable"
        >
          {unavailableReason}
        </Box>
      ) : (
        <BarChart
          series={series}
          xDomain={xDomain}
          xTitle="Day (UTC)"
          yTitle="Count"
          ariaLabel="Questions asked and active users per day"
          statusType={loading ? "loading" : "finished"}
          loadingText="Loading daily activity"
          hideFilter
          empty={
            <Box textAlign="center" padding="l" color="text-body-secondary">
              No activity recorded in this period.
            </Box>
          }
          noMatch={
            <Box textAlign="center" padding="l" color="text-body-secondary">
              No matching data.
            </Box>
          }
        />
      )}
    </Container>
  );
};

export default DailyActivityChart;
